import tw from 'twin.macro'
import { graphql } from 'gatsby'
import { useEffect, useMemo, useRef, useState } from 'react'
import { motion, useMotionValue, animate, useTransform } from 'framer-motion'

import RichText from '@/components/ui/rich-text'
import ImageContentful from '@/components/ui/image-contentful'

import useStickyHelper from '@/utils/useStickyHelper'
import { getOs } from '@/utils/user-info'

export const fragment = graphql`
  fragment Table on ContentfulComponentTable {
    __typename
    contentful_id
    title
    subtext
    stickySections
    headers {
      ...TableCell
    }
    rows {
      ...TableRow
    }
    footer {
      ...TableRow
    }
  }
`

type TableProps = {
  title?: string
  subtext?: string
  headers: Queries.TableCellFragment[]
  rows: Queries.TableRowFragment[]
  footer: Queries.TableRowFragment
  stickySections?: ('Header' | 'First Column')[]
  tableStyle?: 'Default' | 'Levels'
}

export default function Table({
  title,
  subtext,
  headers,
  rows,
  footer,
  stickySections = [],
  tableStyle = 'Default',
}: TableProps) {
  const isHeaderSticky = !!stickySections?.includes('Header')
  const isFirstColumnSticky = !!stickySections?.includes('First Column')

  const styles = useMemo(() => {
    switch (tableStyle) {
      case 'Levels':
        return {
          columnColors: [
            tw`bg-gray-100`,
            tw`bg-white`,
            tw`bg-gray-100`,
            tw`bg-[#EBF0F8]`,
            tw`bg-[#DBE5F6]`,
            tw`bg-[#AAC3EF]`,
          ],
          bodyStyle: [tw`border-b border-primaryBlue`],
          footerStyle: [tw`[td]:(border-t border-b-0 border-primaryBlue)`],
        }
      case 'Default':
      default:
        return {
          columnColors: [tw`bg-white`],
          headerStyle: [
            isHeaderSticky &&
              tw`bg-[#DBE5F6] border-t border-b first-of-type:(border-l rounded-l-lg) last-of-type:(border-r rounded-r-lg)  border-[#AAC3EF]`,
            !isHeaderSticky && tw`bg-white`,
            isFirstColumnSticky &&
              !isHeaderSticky &&
              tw`first-of-type:(bg-[#DBE5F6] border border-b-0 rounded-t-lg border-[#AAC3EF])`,
            isFirstColumnSticky && tw`rounded-bl-none!`,
          ],
          bodyStyle: [tw`border-b border-gray-200`],
          firstColumnStyle: [
            isFirstColumnSticky && !isHeaderSticky && tw`bg-[#DBE5F6] border border-b-0 border-[#AAC3EF]`,
            isFirstColumnSticky && isHeaderSticky && tw`bg-gray-100 border-gray-200 border-l border-b`,
            !isFirstColumnSticky && tw`bg-white`,
            isFirstColumnSticky && !footer && tw`border-r`,
          ],
          lastColumnStyle: [tw`bg-white`],
          footerStyle: [
            tw`[td]:(border-t border-t-[#AAC3EF] border-b-0)`,
            isFirstColumnSticky && tw`[td:first-of-type]:(rounded-b-lg border-b border-b-[#DBE5F6])`,
          ],
          rootStyle: [isFirstColumnSticky && !footer && tw`[tbody tr:last-of-type td:first-of-type]:(rounded-b-lg)`],
          overflowIndicatorStyle: [tw`bg-gradient-to-b from-[#AAC3EF] to-transparent`],
        }
    }
  }, [tableStyle, footer, isHeaderSticky, isFirstColumnSticky])

  const containerRef = useRef<HTMLDivElement>(null)
  const { stickyTracker, top, isStuck } = useStickyHelper({ containerRef, trackerAnchor: 'right' })

  const isMobile = useMemo(() => {
    const os = getOs()
    return os === 'iOS' || os === 'Android' || os === 'Unknown'
  }, [])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const tHeadRef = useRef<HTMLTableSectionElement>(null)
  const scrollX = useMotionValue(0)
  const [dragConstraints, setDragConstraints] = useState<{ left: number; width: number | string } | null>(null)
  const [hasOverflow, setHasOverflow] = useState(isMobile)
  const padding = useRef(0)

  const stickyFirstColumnX = useTransform(scrollX, (value) => {
    // Don't stick until we've scrolled past the padding
    if (value > -padding.current) return undefined
    // If we've scrolled past the padding, stick the first column
    if (value < 0) return Math.abs(value + padding.current)
    // For scrolling back and mimicking the elastic effect
    if (value > 0) return value / 100
    return 0
  })

  useEffect(() => {
    const onResize = () => {
      if (containerRef.current) {
        const { paddingLeft } = window.getComputedStyle(containerRef.current)
        padding.current = parseInt(paddingLeft, 10)
      }
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth } = scrollContainerRef.current
        const isOverflowing = scrollWidth > clientWidth
        setHasOverflow(isOverflowing)
        if (window.getSelection) {
          // Clear highlighted text to prevent it getting stuck in drag
          window.getSelection()?.removeAllRanges()
        }
        scrollX.set(0)
        if (!isOverflowing) {
          setDragConstraints(null)
        } else {
          setDragConstraints({
            left: clientWidth - scrollWidth,
            width: (tableRef.current?.getBoundingClientRect().width ?? 0) + padding.current ?? '100%',
          })
        }
      }
    }
    if (isMobile) {
      // Use orientation change because resize event randomly fires on mobile
      window.screen.orientation.addEventListener('change', onResize)
      onResize()
      return () => {
        window.screen.orientation.removeEventListener('change', onResize)
      }
    }

    window.addEventListener('resize', onResize)
    onResize()

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  const renderBodyCell = (
    cell: Queries.TableCellFragment,
    cellIndex: number,
    row: Queries.TableRowFragment,
    rowIndex: number
  ) => {
    const isFirstColumn = cellIndex === 0
    const isLastColumn = cellIndex === row.cells.length - 1 + (row.rowTitle ? 1 : 0)
    return (
      <motion.td
        key={cell.id}
        css={[
          tw`relative p-6 align-top [p]:leading-tight [*:last-of-type]:mb-0!`,
          isFirstColumn && isFirstColumnSticky && tw`z-[1]`,
          rowIndex < rows.length - 1 && styles.bodyStyle,
          styles.columnColors[cellIndex % styles.columnColors.length],
          isFirstColumn && styles.firstColumnStyle,
          isLastColumn && styles.lastColumnStyle,
        ]}
        style={
          isFirstColumn && isFirstColumnSticky
            ? {
                x: stickyFirstColumnX,
              }
            : undefined
        }
      >
        <div
          css={[isFirstColumn && isFirstColumnSticky && tw`sticky`]}
          style={
            isFirstColumn && isFirstColumnSticky
              ? { top: (isHeaderSticky ? tHeadRef.current?.getBoundingClientRect().height ?? 0 : 0) + top + 16 }
              : undefined
          }
        >
          <RichText body={cell.content as any} />
        </div>
      </motion.td>
    )
  }

  const getScrollEnd = () => dragConstraints?.left ?? 0

  const handleScroll = (e: React.WheelEvent) => {
    const latest = scrollX.get()
    const newValue = Math.min(0, Math.max(latest - e.deltaX, getScrollEnd()))
    scrollX.set(newValue)
  }
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const latest = scrollX.get()
    const distance = 150
    if (e.key === 'ArrowLeft') {
      animate(scrollX, Math.min(0, latest + distance))
    }
    if (e.key === 'ArrowRight') {
      animate(scrollX, Math.max(latest - distance, getScrollEnd()))
    }
  }

  return (
    <section tw="relative max-w-8xl mx-auto px-5 lg:px-9 overflow-clip" ref={containerRef}>
      {title && (
        <h3 tw="text-center" css={subtext ? tw`mb-4` : tw`mb-10`}>
          {title}
        </h3>
      )}
      {subtext && <p tw="text-center mb-10">{subtext}</p>}
      {/* Mask for hiding grid above sticky header */}
      {isHeaderSticky && (
        <motion.div
          tw="sticky left-0 z-[11] w-full pointer-events-none transition-opacity duration-100"
          css={[styles.overflowIndicatorStyle]}
          style={{
            opacity: isStuck ? 1 : 0,
            top,
            x: scrollX,
            width: dragConstraints?.width ?? '100%',
          }}
        >
          <div
            tw="absolute top-0 left-0 z-10 w-full bg-white"
            style={{
              height: top,
              top: -top,
            }}
          />
        </motion.div>
      )}

      <motion.div tw="relative" ref={scrollContainerRef}>
        {stickyTracker}
        <motion.table
          ref={tableRef}
          tw="relative border-separate border-spacing-0 [h2,h3,h4,h5,h6]:font-normal [h5]:text-xl [h6]:text-base min-w-full"
          css={[styles.rootStyle]}
          drag={hasOverflow || isMobile ? 'x' : false}
          draggable={hasOverflow || isMobile}
          dragConstraints={{ left: dragConstraints?.left ?? 0, right: 0 }}
          style={{ x: scrollX }}
          whileHover={hasOverflow && !isMobile ? { cursor: 'grab' } : undefined}
          whileDrag={hasOverflow && !isMobile ? { cursor: 'grabbing' } : undefined}
          tabIndex={!isMobile ? 0 : undefined}
          onWheel={handleScroll}
          onKeyDown={handleKeyDown}
        >
          <thead ref={tHeadRef}>
            <tr css={[isHeaderSticky && tw`sticky z-[2]`]} style={{ top }}>
              {headers.map((header, index) => {
                const isFirstColumn = index === 0
                const isLastColumn = index === headers.length - 1
                return (
                  <motion.th
                    key={header.id}
                    scope="col"
                    css={[
                      tw`p-6 text-left [*:last-of-type]:mb-0!`,
                      styles.bodyStyle,
                      styles.columnColors[index % styles.columnColors.length],
                      isFirstColumn && isFirstColumnSticky && tw`sticky left-0 z-[1]`,
                      isFirstColumn && styles.firstColumnStyle,
                      isLastColumn && styles.lastColumnStyle,
                      styles.headerStyle,
                    ]}
                    style={
                      isFirstColumn && isFirstColumnSticky
                        ? {
                            x: stickyFirstColumnX,
                          }
                        : undefined
                    }
                  >
                    <div className="table-header-cell-container" tw="flex gap-4">
                      {header.icon && (
                        <div
                          className="table-header-icon"
                          tw="w-14 h-14 rounded-md overflow-hidden [svg]:(fill-none w-full h-full)"
                        >
                          <ImageContentful {...header.icon} />
                        </div>
                      )}
                      <div tw="flex-1">
                        <RichText body={header.content as any} />
                      </div>
                    </div>
                  </motion.th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={row.id}>
                {row.rowTitle &&
                  renderBodyCell({ id: 'rowTitle', content: row.rowTitle, icon: null }, 0, row, rowIndex)}
                {row.cells.map((cell, cellIndex) =>
                  renderBodyCell(cell, cellIndex + (row.rowTitle ? 1 : 0), row, rowIndex)
                )}
              </tr>
            ))}
          </tbody>
          {footer && (
            <tfoot>
              <tr css={[styles.footerStyle]}>
                {footer.rowTitle &&
                  renderBodyCell({ id: 'footerTitle', content: footer.rowTitle, icon: null }, 0, footer, 0)}
                {footer.cells.map((cell, index) => {
                  const isFirstColumn = index === 0 && !footer.rowTitle
                  return (
                    <td
                      key={cell.id}
                      css={[
                        tw`relative p-6 align-top border-t border-primaryBlue`,
                        styles.columnColors[(index + (footer.rowTitle ? 1 : 0)) % styles.columnColors.length],
                        isFirstColumn && isFirstColumnSticky && tw`z-[1]`,
                      ]}
                    >
                      <div
                        css={[isFirstColumn && isFirstColumnSticky && tw`sticky`]}
                        style={
                          isFirstColumn && isFirstColumnSticky
                            ? { top: (tHeadRef.current?.getBoundingClientRect().height ?? 0) + top + 16 }
                            : undefined
                        }
                      >
                        <RichText body={cell.content as any} />
                      </div>
                    </td>
                  )
                })}
              </tr>
            </tfoot>
          )}
        </motion.table>
      </motion.div>
    </section>
  )
}
